import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { collection, query, where, getDocs, getDoc, doc, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import ItemFormModal from '../components/ItemFormModal';
import MoveItemModal from '../components/MoveItemModal';
import UsageLimitModal from '../components/UsageLimitModal';
import { ArrowLeftIcon, PencilIcon, TrashIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import IconButton from '../components/shared/IconButton';
import { ActionButtons, ActionContainer } from '../components/shared/ActionButtons';
import {
  SearchResults,
  SearchResultsSection,
  SearchResultsHeader,
  SearchResultItem,
  ItemInfo,
  LocationInfo
} from '../components/shared/SearchResults';
import Tooltip, { TooltipContainer } from '../components/shared/Tooltip';
import PhotoGallery from '../components/PhotoGallery';
import Input from '../components/Input';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  // gap: ${({ theme }) => theme.spacing.sm};
`;

const HeaderInfo = styled.div`
  h1 {
    margin: 0;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    opacity: 0.8;
  }
`;

const BackButton = styled(IconButton)`
  padding: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  
  &:hover {
    background: ${({ theme }) => `${theme.colors.primary}10`};
  }
`;

const SearchContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    width: 100%;

    /* Make the search bar inside take full width */
    > div {
      width: 100%;
    }
  }
`;

const BinSection = styled.div`
  margin: ${({ theme }) => theme.spacing.xl} 0;
`;

const BinGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
    padding: 0;
  }
`;

const BinCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.lg};
  position: relative;
  border-radius: 12px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  min-height: 400px;
  
  /* Main bin container with subtle gradient */
  background: linear-gradient(
    to bottom,
    ${({ theme }) => theme.colors.white} 0%,
    ${({ theme }) => `${theme.colors.secondary}10`} 100%
  );
  
  /* Side walls of the bin */
  &::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors.white};
    border: 2px solid rgba(0, 0, 0, 0.8);
    border-radius: 12px;
    z-index: -1;
    transform: translateY(-4px);
    transition: transform 0.3s ease;
  }

  /* Lid of the bin */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 8px;
    background: linear-gradient(
      to right,
      ${({ theme }) => theme.colors.primary} 0%,
      ${({ theme }) => `${theme.colors.primary}80`} 50%,
      ${({ theme }) => theme.colors.primary} 100%
    );
    border-radius: 12px 12px 0 0;
    transform-origin: center top;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  /* Handle on the lid */
  &:before {
    content: '';
    position: absolute;
    top: 4px;
    left: 50%;
    width: 40px;
    height: 2px;
    background: ${({ theme }) => `${theme.colors.white}80`};
    transform: translateX(-50%);
    border-radius: 2px;
    z-index: 1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  /* Hover effects */
  &:hover {
    transform: translateY(-2px);
    
    &::after {
      transform: rotateX(24deg) translateY(-4px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
    }
    
    &:before {
      opacity: 1;
    }
  }

  /* Inner shadow for depth */
  box-shadow: 
    inset 0 8px 16px ${({ theme }) => `${theme.colors.secondary}10`},
    0 4px 8px rgba(0, 0, 0, 0.05);

  /* Main container border */
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: none;
`;

const BinContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const BinFooter = styled.div`
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BinHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => `${theme.colors.secondary}20`};

  h2 {
    margin: 0;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const BinNameInput = styled.input`
  font-size: inherit;
  font-weight: inherit;
  border: none;
  border-bottom: 1px solid transparent;
  background: transparent;
  padding: 2px;
  margin: 0;
  width: auto;
  min-width: 50px;

  &:hover {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  &:focus {
    outline: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  }
`;

const ItemList = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  max-height: 250px;
  overflow: visible;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  position: relative;
  z-index: 1;

  /* Create an inner scrollable container */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    pointer-events: none;
  }

  /* Styling for the scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => `${theme.colors.secondary}10`};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => `${theme.colors.secondary}40`};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => `${theme.colors.secondary}60`};
    }
  }
`;

const ScrollContainer = styled.div`
  max-height: 250px;
  overflow-y: auto;
  margin: -${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
`;

const ItemCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid #eee;
  position: relative;

  &:last-child {
    border-bottom: none;
  }

  ${TooltipContainer} {
    z-index: 2;
  }
`;

const BinItemInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  img {
    width: 50px;
    height: 50px;
    object-fit: cover;
    border-radius: 4px;
    margin-right: ${({ theme }) => theme.spacing.md};
  }
`;

const BinItemDetails = styled.div`
  h3 {
    margin: 0;
    font-size: 1rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const ItemActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const AddItemForm = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const RoomPhotos = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const RoomPhotoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const FileInput = styled.input`
  display: none;
`;

const RoomItemsSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md};
    margin: 0 -${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
    border-radius: 0;
  }
`;

const BinNumber = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.7;
  margin-top: 2px;
`;

const BinTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const EditBinIcon = styled(IconButton)`
  padding: 2px;
  margin-left: ${({ theme }) => theme.spacing.xs};
  opacity: 0.4;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const AddBinSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  text-align: center;
  border: 2px dashed ${({ theme }) => theme.colors.border};

  p {
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const BinActions = ActionContainer;

const BinItemCount = styled.div`
  text-align: center;

  span {
    display: block;
    font-size: 1.5rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 2px;
  }

  p {
    margin: 0;
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.text};
    opacity: 0.8;
  }
`;

const InventorySearchBar = styled(Input)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  width: 100%;
  max-width: 300px;

  @media (max-width: 768px) {
    max-width: none;
  }
`;

const Inventory = () => {
  const [room, setRoom] = useState(null);
  const [bins, setBins] = useState([]);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({ bins: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [movingItem, setMovingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [editingBinName, setEditingBinName] = useState(null);
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const { user, isPremium } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch room data
        const roomDoc = await getDoc(doc(db, 'rooms', roomId));
        if (!roomDoc.exists()) {
          navigate('/dashboard');
          return;
        }
        setRoom({ id: roomDoc.id, ...roomDoc.data() });

        // Fetch bins
        const binsQuery = query(
          collection(db, 'bins'),
          where('roomId', '==', roomId)
        );
        const binsSnapshot = await getDocs(binsQuery);
        const binsData = binsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBins(binsData);

        // Fetch items
        const itemsQuery = query(
          collection(db, 'items'),
          where('roomId', '==', roomId)
        );
        const itemsSnapshot = await getDocs(itemsQuery);
        const itemsData = itemsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setItems(itemsData);

        // Fetch available rooms for moving items
        const roomsQuery = query(
          collection(db, 'rooms'),
          where('userId', '==', roomDoc.data().userId)
        );
        const roomsSnapshot = await getDocs(roomsQuery);
        const roomsData = roomsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }));
        setAvailableRooms(roomsData);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [roomId, navigate]);

  const handleAddPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const storageRef = ref(storage, `rooms/${roomId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const photoUrl = await getDownloadURL(storageRef);

      const roomRef = doc(db, 'rooms', roomId);
      const photos = room.photos || [];
      await updateDoc(roomRef, {
        photos: [...photos, photoUrl]
      });

      setRoom(prev => ({
        ...prev,
        photos: [...(prev.photos || []), photoUrl]
      }));
    } catch (error) {
      console.error('Error adding photo:', error);
    }
  };

  const handleMoveItem = (item) => {
    setMovingItem(item);
    setIsMoveModalOpen(true);
  };

  const handleMoveSubmit = async (targetRoomId, targetBinId) => {
    if (!movingItem) return;

    try {
      // Ensure consistent bin ID format
      const normalizedBinId = targetBinId === 'room-items' ? '0' : targetBinId;

      // Update item's room and bin
      const itemRef = doc(db, 'items', movingItem.id);
      await updateDoc(itemRef, {
        roomId: targetRoomId,
        binId: normalizedBinId,
        movedAt: new Date().toISOString()
      });

      // If moving to a different room
      if (targetRoomId !== roomId) {
        // Update item counts for both rooms
        const sourceRoomRef = doc(db, 'rooms', roomId);
        const targetRoomRef = doc(db, 'rooms', targetRoomId);

        await updateDoc(sourceRoomRef, {
          itemCount: (room.itemCount || 0) - 1
        });

        await updateDoc(targetRoomRef, {
          itemCount: (availableRooms.find(r => r.id === targetRoomId)?.itemCount || 0) + 1
        });

        // Remove item from local state
        setItems(items.filter(i => i.id !== movingItem.id));
        setRoom(prev => ({
          ...prev,
          itemCount: (prev.itemCount || 0) - 1
        }));
      } else {
        // If moving within the same room, just update the binId
        setItems(items.map(item => 
          item.id === movingItem.id 
            ? { ...item, binId: normalizedBinId }
            : item
        ));
      }

      setIsMoveModalOpen(false);
      setMovingItem(null);
    } catch (error) {
      console.error('Error moving item:', error);
    }
  };

  const handleAddItem = async (e, binId) => {
    e.preventDefault();
    setEditingItem({ binId }); 
    setIsModalOpen(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      // Delete item photos from storage
      if (item.photoUrl) {
        const photoRef = ref(storage, item.photoUrl);
        await deleteObject(photoRef);
      }
      if (item.receiptPhotoUrl) {
        const receiptRef = ref(storage, item.receiptPhotoUrl);
        await deleteObject(receiptRef);
      }

      // Delete item from Firestore
      await deleteDoc(doc(db, 'items', item.id));

      // Update room's item count
      const roomRef = doc(db, 'rooms', roomId);
      await updateDoc(roomRef, {
        itemCount: (room.itemCount || 0) - 1
      });

      // Update local state
      setItems(items.filter(i => i.id !== item.id));
      setRoom(prev => ({
        ...prev,
        itemCount: (prev.itemCount || 0) - 1
      }));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get('itemName');
    const value = formData.get('value');
    const photo = formData.get('photo');
    const receipt = formData.get('receipt');
    const binId = formData.get('binId') || '0';

    try {
      let photoUrl = editingItem?.photoUrl || '';
      let receiptPhotoUrl = editingItem?.receiptPhotoUrl || '';
      
      // Handle item photo
      if (photo.size > 0) {
        if (editingItem?.photoUrl) {
          const oldPhotoRef = ref(storage, editingItem.photoUrl);
          await deleteObject(oldPhotoRef);
        }
        const photoRef = ref(storage, `items/${Date.now()}_${photo.name}`);
        await uploadBytes(photoRef, photo);
        photoUrl = await getDownloadURL(photoRef);
      }

      // Handle receipt photo
      if (receipt.size > 0) {
        if (editingItem?.receiptPhotoUrl) {
          const oldReceiptRef = ref(storage, editingItem.receiptPhotoUrl);
          await deleteObject(oldReceiptRef);
        }
        const receiptRef = ref(storage, `receipts/${Date.now()}_${receipt.name}`);
        await uploadBytes(receiptRef, receipt);
        receiptPhotoUrl = await getDownloadURL(receiptRef);
      }

      if (editingItem?.id) {
        // Update existing item
        const itemRef = doc(db, 'items', editingItem.id);
        await updateDoc(itemRef, {
          name,
          value: value ? parseFloat(value) : null,
          photoUrl,
          receiptPhotoUrl,
          binId,
          updatedAt: new Date().toISOString()
        });

        setItems(items.map(item => 
          item.id === editingItem.id 
            ? { 
                ...item, 
                name, 
                value: value ? parseFloat(value) : null,
                photoUrl,
                receiptPhotoUrl,
                binId,
                updatedAt: new Date().toISOString()
              } 
            : item
        ));
      } else {
        // Add new item
        const newItem = {
          name,
          value: value ? parseFloat(value) : null,
          photoUrl,
          receiptPhotoUrl,
          binId,
          roomId,
          createdAt: new Date().toISOString()
        };

        const itemRef = await addDoc(collection(db, 'items'), newItem);

        // Update room's item count
        const roomRef = doc(db, 'rooms', roomId);
        await updateDoc(roomRef, {
          itemCount: (room.itemCount || 0) + 1
        });

        // Update local state with the new item
        setItems(prevItems => [...prevItems, {
          id: itemRef.id,
          ...newItem
        }]);

        setRoom(prev => ({
          ...prev,
          itemCount: (prev.itemCount || 0) + 1
        }));
      }

      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleBinNameChange = async (binId, newName) => {
    try {
      const binRef = doc(db, 'bins', binId);
      await updateDoc(binRef, { name: newName });
      setBins(bins.map(bin => 
        bin.id === binId ? { ...bin, name: newName } : bin
      ));
    } catch (error) {
      console.error('Error updating bin name:', error);
    }
  };

  const handleSearch = async (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setSearchResults({ bins: [], items: [] });
      return;
    }

    setLoading(true);
    try {
      const searchValue = value.toLowerCase();
      
      // Get all bins for this room
      const binsRef = collection(db, 'bins');
      const binsQuery = query(binsRef, where('roomId', '==', roomId));
      const binsSnapshot = await getDocs(binsQuery);
      
      // Find matching bins
      const matchingBins = binsSnapshot.docs
        .filter(binDoc => {
          const binData = binDoc.data();
          const binName = binData?.name || '';
          const binNumber = binData?.number || '';
          return binName.toLowerCase().includes(searchValue) || 
                 binNumber.toString().includes(searchValue);
        })
        .map(binDoc => ({
          id: binDoc.id,
          ...binDoc.data(),
          room: { id: roomId, name: room?.name }
        }));

      // Get items by name match
      const itemsRef = collection(db, 'items');
      const itemsQuery = query(itemsRef, where('roomId', '==', roomId));
      const itemsSnapshot = await getDocs(itemsQuery);
      const matchingItems = await Promise.all(
        itemsSnapshot.docs
          .filter(doc => {
            const itemData = doc.data();
            const itemName = itemData?.name || '';
            return itemName.toLowerCase().includes(searchValue);
          })
          .map(async (docSnapshot) => {
            const itemData = { id: docSnapshot.id, ...docSnapshot.data() };
            if (itemData.binId) {
              const binDoc = await getDoc(doc(db, 'bins', itemData.binId));
              if (binDoc.exists()) {
                itemData.bin = { id: binDoc.id, ...binDoc.data() };
              }
            }
            return itemData;
          })
      );

      setSearchResults({
        bins: matchingBins,
        items: matchingItems
      });
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = searchTerm
    ? items.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : items;

  const handleAddBin = async () => {
    try {
      // Check bin limit for free users
      if (!isPremium && bins.length >= 5) {
        setShowLimitModal(true);
        return;
      }

      // Find the highest bin number
      const maxBinNumber = bins.reduce((max, bin) => {
        const binNum = parseInt(bin.number) || 0;
        return binNum > max ? binNum : max;
      }, 0);

      // Create new bin with next number
      const newBinRef = await addDoc(collection(db, 'bins'), {
        roomId,
        number: (maxBinNumber + 1).toString(),
        name: '',
        createdAt: new Date().toISOString()
      });

      // Update room's bin count
      const roomRef = doc(db, 'rooms', roomId);
      await updateDoc(roomRef, {
        binCount: (bins.length + 1)
      });

      // Add new bin to state
      const newBin = {
        id: newBinRef.id,
        roomId,
        number: (maxBinNumber + 1).toString(),
        name: '',
        createdAt: new Date().toISOString()
      };
      setBins([...bins, newBin]);
    } catch (error) {
      console.error('Error adding bin:', error);
    }
  };

  const handleDeleteBin = async (binId) => {
    if (!window.confirm('Are you sure you want to delete this bin? All items will be moved to Room Items.')) {
      return;
    }

    try {
      // Move all items in this bin to room items (bin 0)
      const binItems = items.filter(item => item.binId === binId);
      await Promise.all(binItems.map(item => 
        updateDoc(doc(db, 'items', item.id), {
          binId: '0',
          updatedAt: new Date().toISOString()
        })
      ));

      // Delete the bin
      await deleteDoc(doc(db, 'bins', binId));

      // Update room's bin count
      const roomRef = doc(db, 'rooms', roomId);
      await updateDoc(roomRef, {
        binCount: (bins.length - 1)
      });

      // Update local state
      setBins(bins.filter(bin => bin.id !== binId));
      setItems(items.map(item => 
        item.binId === binId 
          ? { ...item, binId: '0' }
          : item
      ));
    } catch (error) {
      console.error('Error deleting bin:', error);
    }
  };

  const handleDeleteItemPhoto = async (photoUrl) => {
    try {
      // Delete photo from storage
      const photoRef = ref(storage, photoUrl);
      await deleteObject(photoRef);

      // Update item document
      const itemRef = doc(db, 'items', editingItem.id);
      await updateDoc(itemRef, {
        photoUrl: '',
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setItems(items.map(item =>
        item.id === editingItem.id
          ? { ...item, photoUrl: '' }
          : item
      ));
      setEditingItem(prev => ({ ...prev, photoUrl: '' }));
    } catch (error) {
      console.error('Error deleting item photo:', error);
    }
  };

  const handleDeleteItemReceipt = async (receiptUrl) => {
    try {
      // Delete receipt from storage
      const receiptRef = ref(storage, receiptUrl);
      await deleteObject(receiptRef);

      // Update item document
      const itemRef = doc(db, 'items', editingItem.id);
      await updateDoc(itemRef, {
        receiptPhotoUrl: '',
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setItems(items.map(item =>
        item.id === editingItem.id
          ? { ...item, receiptPhotoUrl: '' }
          : item
      ));
      setEditingItem(prev => ({ ...prev, receiptPhotoUrl: '' }));
    } catch (error) {
      console.error('Error deleting item receipt:', error);
    }
  };

  if (loading) return <Container>Loading...</Container>;
  if (!room) return <Container>Room not found</Container>;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Tooltip text="Back to Dashboard">
            <BackButton onClick={() => navigate('/dashboard')}>
              <ArrowLeftIcon width={24} />
            </BackButton>
          </Tooltip>
          <HeaderInfo>
            <h1>{room?.name} Inventory</h1>
            <p>{bins.length} bins • {items.length} items</p>
          </HeaderInfo>
        </HeaderLeft>
        <InventorySearchBar
          type="text"
          placeholder="Search items or bins..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </Header>

      {searchTerm && (
        <SearchContainer>
          <SearchResults>
            <SearchResultsSection>
              <SearchResultsHeader>Items ({filteredItems.length})</SearchResultsHeader>
              {filteredItems.map(item => (
                <SearchResultItem key={item.id}>
                  <ItemInfo>
                    <h4>{item.name}</h4>
                    <LocationInfo>
                      Found in {room.name} → Bin {bins.find(bin => bin.id === item.binId)?.number}
                      {bins.find(bin => bin.id === item.binId)?.name && 
                        ` (${bins.find(bin => bin.id === item.binId)?.name})`}
                    </LocationInfo>
                  </ItemInfo>
                  <Button
                    variant="secondary"
                    onClick={() => handleEditItem(item)}
                  >
                    View
                  </Button>
                </SearchResultItem>
              ))}
            </SearchResultsSection>
          </SearchResults>
        </SearchContainer>
      )}

      <RoomPhotos>
        <RoomPhotoHeader>
          <h2>Room Photos</h2>
          <FileInput
            type="file"
            accept="image/*"
            id="room-photo-input"
            onChange={handleAddPhoto}
          />
          <Button onClick={() => document.getElementById('room-photo-input').click()}>
            Add Photo
          </Button>
        </RoomPhotoHeader>
        {room.photos?.length > 0 && (
          <PhotoGallery 
            photos={room.photos} 
            onDelete={async (photoUrl) => {
              if (!window.confirm('Are you sure you want to delete this photo?')) {
                return;
              }
              try {
                // Delete from storage
                const photoRef = ref(storage, photoUrl);
                await deleteObject(photoRef);

                // Update room document
                const roomRef = doc(db, 'rooms', roomId);
                const updatedPhotos = room.photos.filter(url => url !== photoUrl);
                await updateDoc(roomRef, { photos: updatedPhotos });

                // Update local state
                setRoom(prev => ({
                  ...prev,
                  photos: updatedPhotos
                }));
              } catch (error) {
                console.error('Error deleting photo:', error);
              }
            }}
          />
        )}
      </RoomPhotos>

      <RoomItemsSection>
        <BinHeader>
          <h2>Room Items</h2>
          <BinItemCount>
            <span>{items.filter(item => !item.binId || item.binId === '0').length}</span>
            <p>items</p>
          </BinItemCount>
        </BinHeader>
        <ItemList>
          <ScrollContainer>
            {items
              .filter(item => !item.binId || item.binId === '0')
              .map(item => (
                <ItemCard key={item.id}>
                  <BinItemInfo>
                    {item.photoUrl && <img src={item.photoUrl} alt={item.name} />}
                    <BinItemDetails>
                      <h3>{item.name}</h3>
                      {item.value && <p>${item.value.toFixed(2)}</p>}
                    </BinItemDetails>
                  </BinItemInfo>
                  <ActionButtons>
                    <Tooltip text="Edit Item">
                      <IconButton onClick={() => handleEditItem(item)}>
                        <PencilIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip text="Move Item">
                      <IconButton onClick={() => handleMoveItem(item)}>
                        <ArrowsRightLeftIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip text="Delete Item">
                      <IconButton variant="danger" onClick={() => handleDeleteItem(item)}>
                        <TrashIcon />
                      </IconButton>
                    </Tooltip>
                  </ActionButtons>
                </ItemCard>
              ))}
          </ScrollContainer>
        </ItemList>
        <ActionContainer>
          <Button onClick={(e) => handleAddItem(e, '0')}>
            Add Room Item
          </Button>
        </ActionContainer>
      </RoomItemsSection>

      <BinSection>
        <BinGrid>
          {bins
            .filter(bin => bin.number !== 0)
            .sort((a, b) => {
              // First sort by number of items (descending)
              const aItemCount = items.filter(item => item.binId === a.id).length;
              const bItemCount = items.filter(item => item.binId === b.id).length;
              if (aItemCount !== bItemCount) {
                return bItemCount - aItemCount;
              }
              
              // Then sort by bin name if both have names
              if (a.name && b.name) {
                return a.name.localeCompare(b.name);
              }
              
              // If one has a name and the other doesn't, prioritize the named bin
              if (a.name) return -1;
              if (b.name) return 1;
              
              // Finally, sort by bin number
              return a.number - b.number;
            })
            .map(bin => (
              <BinCard key={bin.id}>
                <BinContent>
                  <BinHeader>
                    <h2>
                      {editingBinName === bin.id ? (
                        <BinNameInput
                          type="text"
                          value={bin.name || ''}
                          onChange={(e) => {
                            setBins(bins.map(b => 
                              b.id === bin.id ? { ...b, name: e.target.value } : b
                            ));
                          }}
                          onBlur={(e) => {
                            handleBinNameChange(bin.id, e.target.value);
                            setEditingBinName(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur();
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <BinTitle>
                          {bin.name || `Bin ${bin.number}`}
                          {bin.name && <BinNumber>Bin {bin.number}</BinNumber>}
                        </BinTitle>
                      )}
                    </h2>
                    <BinItemCount>
                      <span>{items.filter(item => item.binId === bin.id).length}</span>
                      <p>items</p>
                    </BinItemCount>
                  </BinHeader>

                  <ItemList>
                    <ScrollContainer>
                      {items
                        .filter(item => item.binId === bin.id)
                        .map(item => (
                          <ItemCard key={item.id}>
                            <BinItemInfo>
                              {item.photoUrl && <img src={item.photoUrl} alt={item.name} />}
                              <BinItemDetails>
                                <h3>{item.name}</h3>
                                {item.value && <p>${item.value.toFixed(2)}</p>}
                              </BinItemDetails>
                            </BinItemInfo>
                            <ActionButtons>
                              <Tooltip text="Edit Item">
                                <IconButton onClick={() => handleEditItem(item)}>
                                  <PencilIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip text="Move Item">
                                <IconButton onClick={() => handleMoveItem(item)}>
                                  <ArrowsRightLeftIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip text="Delete Item">
                                <IconButton variant="danger" onClick={() => handleDeleteItem(item)}>
                                  <TrashIcon />
                                </IconButton>
                              </Tooltip>
                            </ActionButtons>
                          </ItemCard>
                        ))}
                    </ScrollContainer>
                  </ItemList>
                </BinContent>

                <BinFooter>
                  <Button onClick={(e) => handleAddItem(e, bin.id)}>
                    Add Item
                  </Button>
                  <ActionButtons>
                    <Tooltip text="Edit Bin Name">
                      <IconButton onClick={() => setEditingBinName(bin.id)}>
                        <PencilIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip text="Delete Bin">
                      <IconButton variant="danger" onClick={() => handleDeleteBin(bin.id)}>
                        <TrashIcon />
                      </IconButton>
                    </Tooltip>
                  </ActionButtons>
                </BinFooter>
              </BinCard>
            ))}
        </BinGrid>
      </BinSection>

      <AddBinSection>
        <p>Need more storage space?</p>
        <Button onClick={handleAddBin}>
          Add New Bin
        </Button>
      </AddBinSection>

      <ItemFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        onSubmit={handleModalSubmit}
        initialData={editingItem}
        binName={editingItem ? bins.find(bin => bin.id === editingItem.binId)?.name : null}
        binNumber={editingItem ? bins.find(bin => bin.id === editingItem.binId)?.number : null}
        mode={editingItem?.id ? 'edit' : 'add'}
        bins={bins.filter(bin => bin.number !== 0)}
        onBinChange={(binId) => {
          setEditingItem(prev => ({ ...prev, binId }));
        }}
        onDeletePhoto={handleDeleteItemPhoto}
        onDeleteReceipt={handleDeleteItemReceipt}
      />

      <MoveItemModal
        isOpen={isMoveModalOpen}
        onClose={() => {
          setIsMoveModalOpen(false);
          setMovingItem(null);
        }}
        onSubmit={handleMoveSubmit}
        item={movingItem}
        currentBin={bins.find(bin => bin.id === movingItem?.binId) || { number: 0, name: 'Room Items' }}
        availableRooms={availableRooms}
      />

      <UsageLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        type="bins"
      />
    </Container>
  );
};

export default Inventory;